import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
  effect,
  inject,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { SidebarService } from '../../service/sidebar.service';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterModule,
  RouterPreloader,
} from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';

import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../features/auth/application/service/auth.service';
import { Menu } from '../../../features/auth/domain/model/menu.model';
import {
  hugeArchive01,
  hugeArrowDown01,
  hugeBriefcase03,
  hugeBuilding06,
  hugeCalendar03,
  hugeCash02,
  hugeComputerSettings,
  hugeContactBook,
  hugeDashboardBrowsing,
  hugeDashboardSquare01,
  hugeEarth,
  hugeFileEdit,
  hugeFolderUpload,
  hugeInternetAntenna01,
  hugeMailReply02,
  hugeMessageUser01,
  hugeRouter,
  hugeSearchList01,
  hugeSent,
  hugeSettings01,
  hugeTruck,
  hugeUserAccount,
  hugeUserGroup,
  hugeUserShield01,
  hugeWaiters,
  hugeResetPassword,
  hugeUserUnlock02,
  hugeBiometricAccess,
  hugeChartAverage,
  hugeComplaint,
} from '@ng-icons/huge-icons';

type NavItem = {
  name: string;
  iconName: string;
  path?: string;
  subItems?: { name: string; path: string; iconName: string }[];
};

const DEFAULT_MENU_ICON_NAME = 'hugeDashboardSquare01';
const SIDEBAR_ICON_REGISTRY = {
  hugeArchive01,
  hugeArrowDown01,
  hugeBriefcase03,
  hugeBuilding06,
  hugeCalendar03,
  hugeCash02,
  hugeComputerSettings,
  hugeContactBook,
  hugeDashboardBrowsing,
  hugeDashboardSquare01,
  hugeEarth,
  hugeFileEdit,
  hugeFolderUpload,
  hugeInternetAntenna01,
  hugeMailReply02,
  hugeMessageUser01,
  hugeRouter,
  hugeSearchList01,
  hugeSent,
  hugeSettings01,
  hugeTruck,
  hugeUserAccount,
  hugeUserGroup,
  hugeUserShield01,
  hugeWaiters,
  hugeResetPassword,
  hugeUserUnlock02,
  hugeBiometricAccess,
  hugeChartAverage,
  hugeComplaint,
} as const;

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe, NgIcon],
  templateUrl: './app-sidebar.component.html',
  providers: [provideIcons(SIDEBAR_ICON_REGISTRY)],
})
export class AppSidebarComponent {
  // Main nav items
  private readonly authService = inject(AuthService);
  navItems: NavItem[] = this.buildNavItems([]);

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;
  private activeRouteCandidates: string[] = [];
  private pendingNavigationUrl: string | null = null;
  private hasWarmedLazyRoutes = false;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private subscription: Subscription = new Subscription();

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private routerPreloader: RouterPreloader,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      const user = this.authService.user();
      this.navItems = this.buildNavItems(user?.menu ?? []);
      this.setActiveMenuFromRoute();
    });

    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;

    this.updateActiveRouteCandidates(this.router.url);
  }

  ngOnInit() {
    this.warmUpLazyRoutes();

    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.pendingNavigationUrl = event.url;
          this.updateActiveRouteCandidates(event.url, this.router.url);
          this.setActiveMenuFromRoute();
        }

        if (event instanceof NavigationEnd) {
          this.updateActiveRouteCandidates(
            event.urlAfterRedirects ?? event.url,
            this.pendingNavigationUrl,
            this.router.url,
          );
          this.pendingNavigationUrl = null;
          this.setActiveMenuFromRoute();
        }
      }),
    );

    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            this.cdr.detectChanges();
          }
        },
      ),
    );

    this.setActiveMenuFromRoute();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  isChildActive(path: string | undefined): boolean {
    return this.isPathActive(path);
  }

  isParentActive(nav: NavItem): boolean {
    if (nav.subItems?.length) {
      return nav.subItems.some((subItem) => this.isPathActive(subItem.path));
    }

    return this.isPathActive(nav.path);
  }

  private isPathActive(path: string | undefined): boolean {
    const normalizedPath = this.normalizeForComparison(path);
    if (!normalizedPath) {
      return false;
    }

    return this.activeRouteCandidates.some(
      (candidate) =>
        candidate === normalizedPath ||
        candidate.startsWith(`${normalizedPath}/`) ||
        this.matchesOverviewAlias(candidate, normalizedPath),
    );
  }

  private matchesOverviewAlias(candidate: string, menuPath: string): boolean {
    if (!menuPath.endsWith('/overview')) {
      return false;
    }

    const parentPath = menuPath.slice(0, -'/overview'.length);
    return candidate === parentPath;
  }

  toggleSubmenu(section: string, index: number) {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;

      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          this.subMenuHeights[key] = el.scrollHeight;
          this.cdr.detectChanges();
        }
      });
    }
  }

  onSidebarMouseEnter() {
    this.isExpanded$
      .subscribe((expanded) => {
        if (!expanded) {
          this.sidebarService.setHovered(true);
        }
      })
      .unsubscribe();
  }

  private setActiveMenuFromRoute() {
    const activeKey = this.findActiveSubmenuKey();
    if (!activeKey) {
      return;
    }

    this.openSubmenu = activeKey;
    setTimeout(() => {
      const el = document.getElementById(activeKey);
      if (el) {
        this.subMenuHeights[activeKey] = el.scrollHeight;
        this.cdr.detectChanges();
      }
    });
  }

  private findActiveSubmenuKey(): string | null {
    const prefix = 'main';

    for (let i = 0; i < this.navItems.length; i += 1) {
      const nav = this.navItems[i];
      if (!nav.subItems) {
        continue;
      }

      for (const subItem of nav.subItems) {
        if (this.isChildActive(subItem.path)) {
          return `${prefix}-${i}`;
        }
      }
    }

    return null;
  }

  private updateActiveRouteCandidates(...urls: Array<string | undefined | null>) {
    const candidates = new Set<string>();
    urls.forEach((url) => {
      const normalized = this.normalizeForComparison(url ?? undefined);
      if (normalized) {
        candidates.add(normalized);
      }
    });

    if (candidates.size === 0) {
      candidates.add('/');
    }

    this.activeRouteCandidates = Array.from(candidates);
  }

  private normalizeForComparison(path?: string): string | null {
    if (!path) {
      return null;
    }

    const [withoutQuery] = path.split('?');
    let normalized = withoutQuery;
    if (!normalized.startsWith('/')) {
      normalized = `/${normalized}`;
    }

    if (normalized !== '/' && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }

    return normalized;
  }

  onSubmenuClick() {
    this.isMobileOpen$
      .subscribe((isMobile) => {
        if (isMobile) {
          this.sidebarService.setMobileOpen(false);
        }
      })
      .unsubscribe();
  }

  onSingleMenuClick() {
    this.openSubmenu = null;
    this.subMenuHeights = {};

    this.isMobileOpen$
      .subscribe((isMobile) => {
        if (isMobile) {
          this.sidebarService.setMobileOpen(false);
        }
      })
      .unsubscribe();
  }

  private warmUpLazyRoutes() {
    if (this.hasWarmedLazyRoutes) {
      return;
    }

    this.hasWarmedLazyRoutes = true;
    const preload = () => this.routerPreloader.preload();

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(() => preload(), { timeout: 1500 });
      return;
    }

    setTimeout(preload, 500);
  }

  private buildNavItems(userMenus: Menu[]): NavItem[] {
    const baseItem: NavItem = {
      iconName: DEFAULT_MENU_ICON_NAME,
      name: 'MENU.HOME.TITLE',
      path: '/dashboard',
    };

    const mapped = userMenus.map((menu) => ({
      iconName: this.getMenuIcon(menu.icon),
      name: menu.title,
      path: this.hasSubItems(menu) ? undefined : this.normalizePath(menu.url),
      subItems: this.hasSubItems(menu)
        ? menu.submenus?.map((submenu) => ({
            iconName: this.getMenuIcon(submenu.icon),
            name: submenu.title,
            path: this.normalizePath(submenu.url),
          }))
        : undefined,
    }));

    return [baseItem, ...mapped];
  }

  private hasSubItems(menu: Menu): boolean {
    return Array.isArray(menu.submenus) && menu.submenus.length > 0;
  }

  private normalizePath(url: string): string {
    if (!url) {
      return '/dashboard';
    }
    return url.startsWith('/') ? url : `/${url}`;
  }

  private getMenuIcon(icon?: string): string {
    return icon?.trim() ? icon : DEFAULT_MENU_ICON_NAME;
  }
}


